import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ShoppingCart as CartIcon,
  LocalShipping as ShippingIcon,
  History as HistoryIcon,
  Bookmark as BookmarkIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Part {
  id: string;
  name: string;
  partNumber: string;
  category: string;
  compatibility: string[];
  price: number;
  supplier: string;
  leadTime: string;
  inStock: boolean;
}

interface OrderItem {
  partId: string;
  partName: string;
  partNumber: string;
  quantity: number;
  unitPrice: number;
  supplier: string;
  subtotal: number;
}

interface Order {
  id: string;
  items: OrderItem[];
  status: 'draft' | 'submitted' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  totalAmount: number;
  orderDate: Date;
  expectedDelivery: Date | null;
  notes: string;
}

export const PartsOrderInterface: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [openPartDialog, setOpenPartDialog] = useState(false);
  const [selectedPart, setSelectedPart] = useState<Part | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [orders, setOrders] = useState<Order[]>([
    {
      id: 'ORD-001',
      items: [
        {
          partId: '1',
          partName: 'Brake Pads',
          partNumber: 'BP-12345',
          quantity: 2,
          unitPrice: 850.99,
          supplier: 'AutoParts Inc.',
          subtotal: 1701.98,
        },
        {
          partId: '2',
          partName: 'Oil Filter',
          partNumber: 'OF-67890',
          quantity: 5,
          unitPrice: 225.50,
          supplier: 'FilterMaster',
          subtotal: 1127.50,
        },
      ],
      status: 'processing',
      totalAmount: 2829.48,
      orderDate: new Date(2023, 2, 10),
      expectedDelivery: new Date(2023, 2, 17),
      notes: 'Rush order for Toyota Corolla repair',
    },
    {
      id: 'ORD-002',
      items: [
        {
          partId: '3',
          partName: 'Spark Plugs',
          partNumber: 'SP-54321',
          quantity: 8,
          unitPrice: 175.99,
          supplier: 'ElectroParts',
          subtotal: 1407.92,
        },
      ],
      status: 'delivered',
      totalAmount: 1407.92,
      orderDate: new Date(2023, 2, 5),
      expectedDelivery: new Date(2023, 2, 12),
      notes: '',
    },
  ]);

  // Mock parts data with more realistic South African market prices
  const parts: Part[] = [
    {
      id: '1',
      name: 'Brake Pads',
      partNumber: 'BP-12345',
      category: 'Braking System',
      compatibility: ['Toyota', 'Honda', 'Nissan'],
      price: 850.99,
      supplier: 'AutoParts Inc.',
      leadTime: '2-3 days',
      inStock: true,
    },
    {
      id: '2',
      name: 'Oil Filter',
      partNumber: 'OF-67890',
      category: 'Engine',
      compatibility: ['Toyota', 'Lexus', 'Honda'],
      price: 225.50,
      supplier: 'FilterMaster',
      leadTime: '1-2 days',
      inStock: true,
    },
    {
      id: '3',
      name: 'Spark Plugs',
      partNumber: 'SP-54321',
      category: 'Ignition',
      compatibility: ['Ford', 'Chevrolet', 'Dodge'],
      price: 175.99,
      supplier: 'ElectroParts',
      leadTime: '2-4 days',
      inStock: true,
    },
    {
      id: '4',
      name: 'Air Filter',
      partNumber: 'AF-98765',
      category: 'Engine',
      compatibility: ['BMW', 'Mercedes', 'Audi'],
      price: 450.99,
      supplier: 'FilterMaster',
      leadTime: '1-2 days',
      inStock: false,
    },
    {
      id: '5',
      name: 'Alternator',
      partNumber: 'ALT-24680',
      category: 'Electrical',
      compatibility: ['Toyota', 'Honda'],
      price: 3250.99,
      supplier: 'ElectroParts',
      leadTime: '3-5 days',
      inStock: true,
    },
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenPartDialog = (part: Part) => {
    setSelectedPart(part);
    setQuantity(1);
    setOpenPartDialog(true);
  };

  const handleClosePartDialog = () => {
    setOpenPartDialog(false);
    setSelectedPart(null);
  };

  const handleAddToCart = () => {
    if (selectedPart && quantity > 0) {
      const newItem: OrderItem = {
        partId: selectedPart.id,
        partName: selectedPart.name,
        partNumber: selectedPart.partNumber,
        quantity: quantity,
        unitPrice: selectedPart.price,
        supplier: selectedPart.supplier,
        subtotal: selectedPart.price * quantity,
      };

      // Check if part already exists in cart
      const existingItemIndex = cart.findIndex(item => item.partId === selectedPart.id);
      
      if (existingItemIndex >= 0) {
        // Update existing item
        const updatedCart = [...cart];
        updatedCart[existingItemIndex].quantity += quantity;
        updatedCart[existingItemIndex].subtotal = 
          updatedCart[existingItemIndex].quantity * updatedCart[existingItemIndex].unitPrice;
        setCart(updatedCart);
      } else {
        // Add new item
        setCart([...cart, newItem]);
      }
      
      handleClosePartDialog();
    }
  };

  const handleRemoveFromCart = (partId: string) => {
    setCart(cart.filter(item => item.partId !== partId));
  };

  const handlePlaceOrder = () => {
    if (cart.length > 0) {
      const totalAmount = cart.reduce((sum, item) => sum + item.subtotal, 0);
      
      const newOrder: Order = {
        id: `ORD-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
        items: [...cart],
        status: 'submitted',
        totalAmount,
        orderDate: new Date(),
        expectedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        notes: '',
      };
      
      setOrders([newOrder, ...orders]);
      setCart([]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'submitted':
        return 'primary';
      case 'processing':
        return 'info';
      case 'shipped':
        return 'warning';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `R${amount.toFixed(2)}`;
  };

  const filteredParts = parts.filter(part => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      part.name.toLowerCase().includes(query) ||
      part.partNumber.toLowerCase().includes(query) ||
      part.category.toLowerCase().includes(query) ||
      part.supplier.toLowerCase().includes(query) ||
      part.compatibility.some(make => make.toLowerCase().includes(query))
    );
  });

  const cartTotal = cart.reduce((sum, item) => sum + item.subtotal, 0);

  return (
    <DashboardLayout>
      <Box sx={{ p: 3, pb: 10 }}>
        <Typography variant="h4" gutterBottom>
          Parts Order Interface
        </Typography>

        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="Order Parts" icon={<CartIcon />} iconPosition="start" />
          <Tab label="Order History" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>

        {currentTab === 0 ? (
          <Grid container spacing={3}>
            {/* Parts Search */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Search Parts
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Search by part name, number, category, or vehicle make..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />

                  <TableContainer component={Paper} sx={{ overflow: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Part Name</TableCell>
                          <TableCell>Part Number</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Compatibility</TableCell>
                          <TableCell align="right">Price</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>Availability</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredParts.map((part) => (
                          <TableRow key={part.id}>
                            <TableCell>{part.name}</TableCell>
                            <TableCell>{part.partNumber}</TableCell>
                            <TableCell>{part.category}</TableCell>
                            <TableCell>
                              {part.compatibility.slice(0, 2).join(', ')}
                              {part.compatibility.length > 2 && '...'}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(part.price)}</TableCell>
                            <TableCell>{part.supplier}</TableCell>
                            <TableCell>
                              <Chip
                                label={part.inStock ? 'In Stock' : 'Out of Stock'}
                                color={part.inStock ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleOpenPartDialog(part)}
                                disabled={!part.inStock}
                                sx={{ 
                                  whiteSpace: 'nowrap',
                                  minWidth: '32px',
                                  px: 1,
                                  fontSize: '0.75rem'
                                }}
                              >
                                + Cart
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Shopping Cart */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Shopping Cart
                  </Typography>
                  
                  {cart.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <CartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        Your cart is empty
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <TableContainer component={Paper} sx={{ mb: 2, overflow: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Part</TableCell>
                              <TableCell align="right">Qty</TableCell>
                              <TableCell align="right">Price</TableCell>
                              <TableCell align="right">Subtotal</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {cart.map((item) => (
                              <TableRow key={item.partId}>
                                <TableCell>
                                  <Typography variant="body2">{item.partName}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.partNumber}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                                <TableCell align="right">{formatCurrency(item.subtotal)}</TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveFromCart(item.partId)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">Total:</Typography>
                        <Typography variant="h6">{formatCurrency(cartTotal)}</Typography>
                      </Box>

                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<ShippingIcon />}
                        onClick={handlePlaceOrder}
                      >
                        Place Order
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {/* Order History */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Order History
                  </Typography>
                  
                  <TableContainer component={Paper} sx={{ overflow: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Order ID</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Items</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Expected Delivery</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orders.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.id}</TableCell>
                            <TableCell>{formatDate(order.orderDate)}</TableCell>
                            <TableCell>{order.items.length} items</TableCell>
                            <TableCell align="right">{formatCurrency(order.totalAmount)}</TableCell>
                            <TableCell>
                              <Chip
                                label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                color={getStatusColor(order.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{formatDate(order.expectedDelivery)}</TableCell>
                            <TableCell>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Add to Cart Dialog */}
        <Dialog open={openPartDialog} onClose={handleClosePartDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add to Cart</DialogTitle>
          <DialogContent>
            {selectedPart && (
              <Box sx={{ pt: 2 }}>
                <Typography variant="h6">{selectedPart.name}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedPart.partNumber} - {selectedPart.category}
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Price:</Typography>
                    <Typography variant="h6">{formatCurrency(selectedPart.price)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Supplier:</Typography>
                    <Typography variant="body1">{selectedPart.supplier}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Lead Time:</Typography>
                    <Typography variant="body1">{selectedPart.leadTime}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Compatibility:</Typography>
                    <Typography variant="body1">
                      {selectedPart.compatibility.join(', ')}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <TextField
                  label="Quantity"
                  type="number"
                  fullWidth
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{ mt: 2 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="h6">
                    {formatCurrency(selectedPart.price * quantity)}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePartDialog}>Cancel</Button>
            <Button onClick={handleAddToCart} variant="contained">
              Add to Cart
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 